import {
  COI_PERMISSIONS,
  COI_REGISTER_TAB_PERMISSIONS,
  COI_REGISTER_TABS,
  COI_SECTION_ORDER,
  COI_SECTION_PATHS,
  COI_SECTION_PERMISSIONS,
  CoiRegisterTab
} from '@/features/coi/constants/coiPermissions';
import { CoiSection } from '@/features/coi/types/coi.types';
import PermissionClassification from '@/util/enums/PermissionClassification';
import useAllowedPermission from '@/util/hooks/useAllowedPermission';

export interface CoiPermissions {
  /** True until the permission list has resolved for the first time. */
  isLoading: boolean;
  /** Whether the COI module should be reachable at all — any one of the eight (AC2). */
  hasAnyCoiPermission: boolean;
  /** Whether a given sidebar section may be opened; Registers is any-of its three (AC4). */
  canOpenSection: (section: CoiSection) => boolean;
  /**
   * A single permission, fail-closed while loading. For callers that need finer granularity than a
   * section — the setup checklist gates individual steps on individual register permissions,
   * because "can open Registers" is any-of three and too coarse to decide whether the employee
   * register's row count may be shown.
   */
  holdsPermission: (permission: PermissionClassification) => boolean;
  /** The register tabs this user may see, in display order (AC5). */
  permittedRegisterTabs: CoiRegisterTab[];
  /**
   * Where the COI navigation entry should point: the first section this user may open, in sidebar
   * order (AC2). Null when the module is not reachable at all, or while loading — callers should be
   * hiding the entry in both those cases anyway, so there is no honest path to send them down.
   */
  landingPath: string | null;
}

/**
 * PBI #7751 — the single place the COI UI asks "may this user see X".
 *
 * FAILS CLOSED WHILE LOADING, deliberately, and that is the main reason this hook exists rather than
 * call sites using `useAllowedPermission` directly. That hook defaults its data to `[]` internally,
 * so its predicate returns `false` while loading AND when genuinely denied — the two are
 * indistinguishable from the predicate alone. Every call site would have to remember to branch on
 * `isLoading` first, and the one that forgets does not fail loudly: it renders a forbidden tab for a
 * moment and then withdraws it, which AC9 calls out specifically as both a disclosure and a support
 * call. Answering `false` to everything until the list resolves makes forgetting harmless; callers
 * that want a spinner still have `isLoading`.
 *
 * NOTE ON THE SOURCE: this reads the LIVE permission list (GET /Permissions/User), while the backend
 * (#7750) gates on the `extension_permissions` token claim, which is frozen at sign-in. The two can
 * disagree — a permission granted mid-session shows the tab here while the API still refuses it.
 * That skew is ADO #7699 and is already mitigated by useAllowedPermission's onPermissionDenied
 * refetch; it is not introduced by this hook. #7751's Technical Document describes this query as
 * reading the token claim, which is not accurate.
 */
export const useCoiPermissions = (): CoiPermissions => {
  const { handleHasPermissionCheckFunction: hasPermission, isLoading } =
    useAllowedPermission();

  const holds = (permission: number) => !isLoading && hasPermission(permission);
  const canOpenSection = (section: CoiSection) =>
    COI_SECTION_PERMISSIONS[section].some(holds);

  const firstPermittedSection = COI_SECTION_ORDER.find(canOpenSection);

  return {
    isLoading,
    hasAnyCoiPermission: COI_PERMISSIONS.some(holds),
    canOpenSection,
    holdsPermission: holds,
    permittedRegisterTabs: COI_REGISTER_TABS.filter((tab) =>
      holds(COI_REGISTER_TAB_PERMISSIONS[tab])
    ),
    landingPath: firstPermittedSection ? COI_SECTION_PATHS[firstPermittedSection] : null
  };
};
